import type { ApplicationContext } from '@hc-frontend/core-context';
import { fetchBasedCall } from '@hc-frontend/core-utils-net';

/**
 * Error thrown when the AdButler API returns a non-2xx response.
 */
export class AdButlerApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly endpoint: string,
    message: string,
  ) {
    super(`AdButler API error [${status}] ${endpoint}: ${message}`);
    this.name = 'AdButlerApiError';
  }
}

export interface AdButlerPortalApi {
  get<T>(
    path: string,
    queryParams?: Record<string, string | number | undefined>,
  ): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  delete(path: string): Promise<void>;
  postMultipart<T>(path: string, formData: FormData): Promise<T>;
}

/**
 * Factory that creates an AdButler Portal API caller.
 * Follows the same pattern as getAdButlerAds — receives baseUrl, apiKey, and log
 * from the server composition root; never exposes credentials to the client.
 */
export function createAdButlerPortalApi(
  baseUrl: string,
  apiKey: string,
  log: ApplicationContext['log'],
): AdButlerPortalApi {
  const normalizedBase = baseUrl.replace(/\/$/, '');
  const authHeader = `Basic ${apiKey}`;

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    queryParams?: Record<string, string | number | undefined>,
  ): Promise<T> {
    const url = `${normalizedBase}${path}`;

    const filteredQuery: Record<string, string> = {};
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined) {
          filteredQuery[key] = String(value);
        }
      }
    }

    log({
      type: 'info',
      context: 'server',
      message: 'adbutlerPortalApiRequest',
      data: {
        method,
        path,
        queryParams: filteredQuery,
        ...(body !== undefined && { body }),
      },
    });

    const result = await fetchBasedCall<unknown, T>({
      url,
      method: method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      body: body as unknown,
      headers: { Authorization: authHeader },
      queryParams:
        Object.keys(filteredQuery).length > 0 ? filteredQuery : undefined,
    });

    if (result.status === 204) {
      return undefined as T;
    }

    if (result.status < 200 || result.status >= 300) {
      const errorBody = result.body as {
        errors?: { message: string }[];
        message?: string;
        parameters?: unknown;
      };
      const message =
        errorBody?.errors?.[0]?.message ??
        errorBody?.message ??
        String(result.status);

      log({
        type: 'error',
        context: 'server',
        message: 'adbutlerPortalApiError',
        data: {
          method,
          path,
          status: result.status,
          errorMessage: message,
          parameters: errorBody?.parameters,
          rawBody: errorBody,
        },
      });

      throw new AdButlerApiError(result.status, path, message);
    }

    log({
      type: 'info',
      context: 'server',
      message: 'adbutlerPortalApiSuccess',
      data: { method, path, status: result.status },
    });

    return result.body as T;
  }

  return {
    get<T>(
      path: string,
      queryParams?: Record<string, string | number | undefined>,
    ): Promise<T> {
      return request<T>('GET', path, undefined, queryParams);
    },

    post<T>(path: string, body: unknown): Promise<T> {
      return request<T>('POST', path, body);
    },

    put<T>(path: string, body: unknown): Promise<T> {
      return request<T>('PUT', path, body);
    },

    delete(path: string): Promise<void> {
      return request<void>('DELETE', path);
    },

    async postMultipart<T>(path: string, formData: FormData): Promise<T> {
      const url = `${normalizedBase}${path}`;

      log({
        type: 'info',
        context: 'server',
        message: 'adbutlerPortalApiRequest',
        data: { method: 'POST multipart', path },
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: authHeader,
          // Content-Type must NOT be set — fetch sets it with the boundary
        },
        body: formData,
      });

      if (!response.ok) {
        let message = response.statusText;
        let parameters: unknown;
        try {
          const err = await response.json();
          message = err?.errors?.[0]?.message ?? err?.message ?? message;
          parameters = err?.parameters;
        } catch {
          // ignore JSON parse error
        }

        log({
          type: 'error',
          context: 'server',
          message: 'adbutlerPortalApiError',
          data: {
            method: 'POST multipart',
            path,
            status: response.status,
            errorMessage: message,
            ...(parameters !== undefined && { parameters }),
          },
        });

        throw new AdButlerApiError(response.status, path, message);
      }

      log({
        type: 'info',
        context: 'server',
        message: 'adbutlerPortalApiSuccess',
        data: { method: 'POST multipart', path, status: response.status },
      });

      return response.json() as Promise<T>;
    },
  };
}
